/** A dashboard tile: **a view over one existing read, never a source** (ADR-0195, option A).
 *
 * Three states, and a tile cannot draw a fourth: still reading (a skeleton shaped like the tile),
 * could not read (the shared `ReadFailed`, with a way to ask again - never an empty chart or a
 * zero), and read. `data-state` carries the state so the screen test and the browser gate assert
 * the tile rather than its prose. A tile that needs two reads passes both; one failure fails the
 * tile, because half a chart is a number with no context.
 */

import type { ReactNode } from "react";

import { ReadFailed } from "../../components/Loaded";
import { Panel } from "../../components/Panel";
import { Skeleton } from "../../components/ui/Skeleton";

export interface TileRead {
  isPending: boolean;
  error: unknown;
  refetch: () => unknown;
}

export function Tile({
  id,
  title,
  sub,
  of,
  reads,
  column,
  action,
  asTable = false,
  children,
}: {
  /** The tile's stable name: `executive-filings`. */
  id: string;
  title: string;
  sub?: string;
  /** What a reader would say was being read, for the failure sentence: "The filings". */
  of: string;
  reads: readonly TileRead[];
  column?: string;
  action?: ReactNode;
  /** The block is the tab's table and not one of its tiles (ADR-0212: a data tab is tiles, one chart
   * and one table). It keeps the three states and the test ids; it is marked `data-table-block`, so
   * the tile count of a tab is the count of its tiles. */
  asTable?: boolean;
  children: ReactNode;
}) {
  const failed = reads.find((read) => read.error);
  const pending = !failed && reads.some((read) => read.isPending);
  const state = failed ? "failed" : pending ? "loading" : "loaded";
  return (
    <Panel title={title} sub={sub} column={column} action={action}>
      <div
        data-testid={`dash-tile-${id}`}
        {...(asTable ? { "data-table-block": id } : { "data-tile": id })}
        data-state={state}
      >
        {failed ? (
          <ReadFailed of={of} error={failed.error} onRetry={() => void failed.refetch()} />
        ) : pending ? (
          <div role="status" aria-busy="true">
            <span className="visuallyHidden">Reading {of.toLowerCase()}…</span>
            <Skeleton width="70%" height={14} />
            <Skeleton width="100%" height={60} style={{ marginTop: 10 }} />
          </div>
        ) : (
          children
        )}
      </div>
    </Panel>
  );
}
